/**
 * DataHealthTab — Org Settings → Data Health: duplicate count, last
 * consolidation run, pending undo, and re-run (merge all) button.
 */
import { useCallback, useEffect, useState } from 'react';
import { HeartPulse, RefreshCw, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MergeAllDialog, { UndoMergeBar } from '../trainees/MergeAllDialog';
import { ReconcileResultsDialog } from '../trainees/ReconcileResultsDialog';

const RUN_LABEL = {
  'trainee.auto_consolidated': 'Automatic (deploy)',
  'trainee.merge_all': 'Merge all (manual)',
  'batch.duplicates_merged': 'Batch page merge',
};

function Stat({ label, value, testId }) {
  return (
    <div className="border rounded-md p-4" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-2xl mt-1">{value}</p>
    </div>
  );
}

export default function DataHealthTab() {
  const { get } = useApi();
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [openReconcile, setOpenReconcile] = useState(false);

  const load = useCallback(() => {
    get('/trainees/duplicates/health').then(setHealth).catch(() => setHealth(null));
  }, [get]);
  useEffect(() => { load(); }, [load]);

  const last = health?.last_run;
  const uc = health?.uncredited || { uncredited: 0, agents: 0, batches: 0, unmatched_attempts: 0, batch_names: [] };
  return (
    <div className="space-y-6" data-testid="data-health-tab">
      <div>
        <h2 className="font-display text-lg flex items-center gap-2"><HeartPulse className="w-5 h-5" /> Data health</h2>
        <p className="text-sm text-muted-foreground">One person, one record — duplicates split scores across the leaderboard, certifications and exports.</p>
      </div>
      {!health ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="People with duplicates" value={health.duplicate_groups} testId="health-dup-groups" />
            <Stat label="Extra records" value={health.duplicate_records} testId="health-dup-records" />
            <Stat label="Trainee records" value={health.total_trainees} testId="health-total" />
          </div>
          <Card className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm" data-testid="health-last-run">
                <p className="font-medium">Last consolidation</p>
                {last ? (
                  <p className="text-muted-foreground">
                    {RUN_LABEL[last.action] || last.action} · {new Date(last.created_at).toLocaleString()} · {last.details?.summary || `${last.details?.people ?? 0} people`}
                  </p>
                ) : <p className="text-muted-foreground">No consolidation has run yet.</p>}
              </div>
              <Button onClick={() => setOpen(true)} disabled={health.duplicate_groups === 0} data-testid="health-rerun-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> {health.duplicate_groups ? `Merge ${health.duplicate_groups} now` : 'Nothing to merge'}
              </Button>
            </div>
            <UndoMergeBar pending={health.pending_undo} onUndone={load} />
          </Card>

          <Card className="p-4 space-y-3" data-testid="health-uncredited-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Uncredited results</p>
                <p className="text-muted-foreground" data-testid="health-uncredited-line">
                  {uc.uncredited === 0
                    ? 'Every submitted assessment is credited to an agent on its batch roster.'
                    : `${uc.uncredited} submitted assessment${uc.uncredited === 1 ? '' : 's'} for ${uc.agents} agent${uc.agents === 1 ? '' : 's'} ${uc.uncredited === 1 ? 'is' : 'are'} not credited to anyone on the roster (${uc.batches} batch${uc.batches === 1 ? '' : 'es'}: ${uc.batch_names.join(', ')}).`}
                  {uc.unmatched_attempts > 0 && ` ${uc.unmatched_attempts} more could not be matched to any agent.`}
                </p>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-display text-2xl tabular-nums" data-testid="health-uncredited-count">{uc.uncredited}</span>
                <Button onClick={() => setOpenReconcile(true)} disabled={uc.uncredited === 0} data-testid="health-reconcile-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> {uc.uncredited ? 'Reconcile all batches' : 'Nothing to restore'}
                </Button>
              </div>
            </div>
          </Card>

          {health.failed_merges?.length > 0 && (
            <Card className="p-4 space-y-2" data-testid="health-failed-merges">
              <p className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Merges that did not complete</p>
              <p className="text-xs text-muted-foreground">These pairs were rolled back untouched. Fix the cause and merge again from the Duplicates tool.</p>
              <ul className="text-sm divide-y border rounded-md">
                {health.failed_merges.map(f => (
                  <li key={f.primary_id} className="px-3 py-2 flex items-baseline justify-between gap-3" data-testid={`failed-merge-${f.primary_id}`}>
                    <span className="truncate">{f.name || f.email} <span className="text-muted-foreground">· {f.dup_ids.length} record{f.dup_ids.length === 1 ? '' : 's'}</span></span>
                    <span className="text-xs text-muted-foreground truncate max-w-[50%]" title={f.error}>{f.error}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
      <MergeAllDialog open={open} onOpenChange={setOpen} onDone={load} />
      <ReconcileResultsDialog open={openReconcile} onOpenChange={setOpenReconcile} scope={{ org: true }} onDone={load} />
    </div>
  );
}
